import React, { useEffect, useRef } from "react";
import "./About.css";
import Header from "../Header/Header";
import Footer from "../Footer/Footer";

/* ── Intersection observer for scroll animations ── */
function useFadeInUp() {
  const ref = useRef(null);
  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    const els = node.querySelectorAll(".fade-in-up");
    const obs = new IntersectionObserver(
      (entries) =>
        entries.forEach((e) => {
          if (e.isIntersecting) e.target.classList.add("visible");
        }),
      { threshold: 0.1 },
    );
    els.forEach((el) => obs.observe(el));
    return () => obs.disconnect();
  }, []);
  return ref;
}

/* ── Data ── */
const stats = [
  { number: "12", suffix: "+", label: "Years of Experience" },
  { number: "99", suffix: "%", label: "Customer Delight" },
  { number: "5000", suffix: "+", label: "Vehicles Serviced" },
  { number: "15", suffix: "+", label: "Expert Technicians" },
];

const pillars = [
  {
    tag: "Vision",
    title: "Our Vision",
    desc: "To be Hamilton's most trusted and innovative automotive workshop, known for exceptional service, integrity, and a commitment to keeping our community safely on the road.",
    img: "/assets/about/vision-bg.webp",
    imgAlt: "Automotive diagnostic tablet in workshop",
    icon: (
      <svg viewBox="0 0 24 24">
        <circle cx="12" cy="12" r="3" />
        <path d="M2 12s4-7 10-7 10 7 10 7-4 7-10 7-10-7-10-7z" />
      </svg>
    ),
    num: "01",
    imgPosition: "center 40%",
    values: null,
  },
  {
    tag: "Mission",
    title: "Our Mission",
    desc: "To deliver high-quality, reliable automotive services with honesty, expertise, and care. We build long-term relationships through transparent communication, modern diagnostics, and a passion for automotive excellence.",
    img: "/assets/about/mission-bg.png",
    imgAlt: "EZAR Automotive workshop facility with Cybertruck",
    icon: (
      <svg viewBox="0 0 24 24">
        <path d="M12 2l3 6.3 6.9 1-5 4.8 1.2 6.9L12 17.8l-6.1 3.2 1.2-6.9-5-4.8 6.9-1z" />
      </svg>
    ),
    num: "02",
    imgPosition: "center 82%",
    values: null,
  },
  {
    tag: "Values",
    title: "Our Values",
    desc: "Every decision we make is guided by a core set of principles that put our customers and community first.",
    img: "/assets/about/values-bg.png",
    imgAlt: "EZAR vehicle servicing and excellence",
    icon: (
      <svg viewBox="0 0 24 24">
        <path d="M9 12l2 2 4-4" />
        <circle cx="12" cy="12" r="9" />
      </svg>
    ),
    num: "03",
    imgPosition: "center center",
    values: [
      "Honesty & Transparency",
      "Technical Excellence",
      "Customer-First Always",
      "Community Commitment",
      "Continuous Innovation",
    ],
  },
];

const team = [
  {
    name: "James Robertson",
    role: "Founder & Master Technician",
    img: "/assets/about/team1.jpg",
  },
  {
    name: "Sarah Kim",
    role: "Head of Detailing Services",
    img: "/assets/about/team2.jpg",
  },
  {
    name: "Mike Taylor",
    role: "Senior Diagnostic Engineer",
    img: "/assets/about/team3.jpg",
  },
];

const whyItems = [
  {
    title: "Certified Experts",
    desc: "Our technicians are fully certified and trained on the latest automotive systems and diagnostics technology.",
    icon: (
      <svg viewBox="0 0 24 24">
        <path d="M9 12l2 2 4-4" />
        <circle cx="12" cy="12" r="9" />
      </svg>
    ),
  },
  {
    title: "Modern Diagnostics",
    desc: "We use cutting-edge diagnostic equipment to identify issues accurately the first time, every time.",
    icon: (
      <svg viewBox="0 0 24 24">
        <rect x="2" y="3" width="20" height="14" rx="2" />
        <path d="M8 21h8M12 17v4" />
      </svg>
    ),
  },
  {
    title: "Transparent Pricing",
    desc: "No hidden fees, no surprises. Clear, upfront pricing before any work begins on your vehicle.",
    icon: (
      <svg viewBox="0 0 24 24">
        <circle cx="12" cy="12" r="9" />
        <path d="M12 6v6l4 2" />
      </svg>
    ),
  },
  {
    title: "Quality Guaranteed",
    desc: "Every service comes with our satisfaction guarantee. We stand behind our work and your peace of mind.",
    icon: (
      <svg viewBox="0 0 24 24">
        <path d="M12 2l3 6.3 6.9 1-5 4.8 1.2 6.9L12 17.8l-6.1 3.2 1.2-6.9-5-4.8 6.9-1z" />
      </svg>
    ),
  },
];

const highlights = [
  "State-of-the-art workshop",
  "OEM & premium parts only",
  "Same-day service available",
  "Fleet & commercial vehicles",
  "Warranty on all repairs",
  "Free vehicle health check",
];

/* ══════════════════════════════════════════
   COMPONENT
══════════════════════════════════════════ */
export default function About() {
  const pageRef = useFadeInUp();

  return (
    <>
      <Header />
      <div className="about-page" ref={pageRef}>
        {/* ── HERO ── */}
        <section className="about-hero">
          <div className="about-hero-bg-text">EZER</div>
          <div className="about-hero-content">
            <nav className="about-hero-breadcrumb fade-in-up">
              <a href="/">Home</a>
              <span className="sep">/</span>
              <span className="current">About Us</span>
            </nav>
            <div
              className="about-hero-label fade-in-up"
              style={{ transitionDelay: "0.05s" }}
            >
              Who We Are
            </div>
            <h1 className="fade-in-up" style={{ transitionDelay: "0.1s" }}>
              About <span>Ezer</span>
              <br />
              Automotive
            </h1>
          </div>
        </section>
        <div className="section-divider" />

        {/* ── STATS ── */}
        <div className="about-stats">
          {stats.map((s, i) => (
            <div
              className="about-stat-item fade-in-up"
              key={i}
              style={{ transitionDelay: `${i * 0.08}s` }}
            >
              <div className="stat-number">
                {s.number}
                <span>{s.suffix}</span>
              </div>
              <div className="stat-label">{s.label}</div>
            </div>
          ))}
        </div>

        {/* ══════════════════════════════════════════
            VISION / MISSION / VALUES
        ══════════════════════════════════════════ */}
        <section className="about-pillars">
          {/* Intro row */}
          <div className="pillars-intro">
            <div className="pillars-intro-left">
              <div className="section-tag-line fade-in-up">Our Foundation</div>
              <h2 className="fade-in-up" style={{ transitionDelay: "0.08s" }}>
                Driven by <em>Purpose,</em>
                <br />
                Defined by
                <br />
                <em>Excellence</em>
              </h2>
              <p className="fade-in-up" style={{ transitionDelay: "0.14s" }}>
                At Ezer Automotive, everything we do is rooted in a clear sense
                of direction. Our vision, mission, and values aren't just words
                on a wall — they shape every interaction, every service, and
                every relationship we build with the Hamilton community.
              </p>
            </div>
            <div
              className="pillars-intro-right fade-in-up"
              style={{ transitionDelay: "0.1s" }}
            >
              {/* hero-img.jpg is the wide workshop/car photo in your about folder */}
              <img
                src="/assets/about/pillars-bg.jpg"
                alt="Automotive workshop"
              />
              <div className="img-badge">
                <span className="badge-num">2012</span>
                <span className="badge-text">Est.</span>
              </div>
            </div>
          </div>

          {/* Three pillar cards */}
          <div className="pillars-cards">
            {pillars.map((p, i) => (
              <div
                className="pillar-card fade-in-up"
                key={i}
                style={{ transitionDelay: `${i * 0.1}s` }}
              >
                <div className="pillar-image">
                  <img
                    src={p.img}
                    alt={p.imgAlt}
                    style={{ objectPosition: p.imgPosition || "center center" }}
                  />
                  <div className="pillar-image-overlay" />
                  <div className="pillar-number">{p.num}</div>
                </div>
                <div className="pillar-body">
                  <div className="pillar-icon-wrap">{p.icon}</div>
                  <div className="pillar-tag">{p.tag}</div>
                  <h3>{p.title}</h3>
                  <div className="pillar-divider" />
                  <p>{p.desc}</p>
                  {p.values && (
                    <ul className="pillar-values" style={{ marginTop: "16px" }}>
                      {p.values.map((v, j) => (
                        <li key={j}>{v}</li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* ── OUR STORY ── */}
        <section className="about-story">
          <div className="story-image-side">
            {/* our-story.jpg is the workshop interior shot */}
            <img
              src="/assets/about/our-story.jpg"
              alt="Ezer Automotive workshop"
            />
            <div className="year-badge">
              <span className="year-num">12+</span>
              <span className="year-text">Years</span>
            </div>
          </div>
          <div className="story-content-side">
            <div className="section-tag-line fade-in-up">Our Journey</div>
            <h2 className="fade-in-up" style={{ transitionDelay: "0.08s" }}>
              Built on Trust,
              <br />
              Driven by <em>Excellence</em>
            </h2>
            <p className="fade-in-up" style={{ transitionDelay: "0.12s" }}>
              Founded in 2012, Ezer Automotive was born from a simple belief —
              every vehicle owner deserves honest, high-quality automotive care
              without compromise. What started as a small workshop in Hamilton
              has grown into one of the region's most trusted service centres.
            </p>
            <p className="fade-in-up" style={{ transitionDelay: "0.16s" }}>
              Over more than a decade, we've built lasting relationships with
              thousands of customers, earning a reputation for transparency,
              technical excellence, and genuine care for the people we serve.
            </p>
            <div
              className="story-highlights fade-in-up"
              style={{ transitionDelay: "0.2s" }}
            >
              {highlights.map((h, i) => (
                <div className="highlight-item" key={i}>
                  <div className="highlight-dot" />
                  <span>{h}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── TEAM ── */}
        {/* <section className="about-team">
          <div className="section-header fade-in-up">
            <div className="section-header-left">
              <div className="section-tag-line">The People Behind Ezer</div>
              <h2>Meet Our Expert Team</h2>
            </div>
          </div>
          <div className="team-grid">
            {team.map((m, i) => (
              <div
                className="team-card fade-in-up"
                key={i}
                style={{ transitionDelay: `${i * 0.1}s` }}
              >
                <div className="team-card-image">
                  <img src={m.img} alt={m.name} />
                </div>
                <div className="team-card-info">
                  <h3>{m.name}</h3>
                  <div className="role">{m.role}</div>
                </div>
              </div>
            ))}
          </div>
        </section> */}

        {/* ── WHY CHOOSE US ── */}
        <section className="about-why">
          <div className="section-header fade-in-up">
            <div className="section-header-left">
              <div className="section-tag-line">Why Ezer</div>
              <h2>Why Choose Us</h2>
            </div>
          </div>
          <div className="why-grid">
            {whyItems.map((item, i) => (
              <div
                className="why-card fade-in-up"
                key={i}
                style={{ transitionDelay: `${i * 0.08}s` }}
              >
                <div className="why-icon">{item.icon}</div>
                <h3>{item.title}</h3>
                <p>{item.desc}</p>
              </div>
            ))}
          </div>
        </section>

        {/* ── CTA ── */}
        <section className="about-cta">
          <h2 className="fade-in-up">
            Ready to experience
            <br />
            <span>automotive excellence?</span>
          </h2>
          <a
            href="/contact"
            className="cta-btn fade-in-up"
            style={{ transitionDelay: "0.12s" }}
          >
            Book an Appointment
            <svg viewBox="0 0 24 24">
              <path d="M5 12h14M12 5l7 7-7 7" />
            </svg>
          </a>
        </section>
      </div>
      <Footer />
    </>
  );
}
